/**
 * Brief 80 — ControlTestForm modal
 *
 * MLCO/Director-only form. Two modes:
 *   - "schedule": POST /api/policy/<slug>/control-test with due_by + reason
 *   - "record": POST /api/policy/control-test/<id>/record with outcome + reason
 */

import React, { useState } from "react";
import { X, Activity, AlertCircle } from "lucide-react";
import { authFetch } from "../../config/api";

// BM-H3260 (29 Sep 2026): authFetch, not raw fetch. Raw fetch
// sends no X-Bonsai-User-Id, so this call was authenticated only when the
// browser held a bonsai_session cookie, which a password sign-in does not
// set. The /policy/* surface was moved to authFetch for exactly this
// (see config/api.js); this file was missed.
const API = process.env.REACT_APP_BACKEND_URL || "";

export default function ControlTestForm({ onClose }) {
  const [mode, setMode] = useState("schedule");
  const [slug, setSlug] = useState("aml-policy");
  const [testRef, setTestRef] = useState("");
  const [title, setTitle] = useState("");
  const [descriptionMd, setDescriptionMd] = useState("");
  const [dueBy, setDueBy] = useState(new Date().toISOString().slice(0, 10));
  const [controlTestId, setControlTestId] = useState("");
  const [outcome, setOutcome] = useState("pass");
  const [testedBy, setTestedBy] = useState("");
  const [evidenceLink, setEvidenceLink] = useState("");
  const [notesMd, setNotesMd] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  async function submit() {
    if (!reason.trim()) {
      setError("Reason is required (audit log invariant)");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      if (mode === "schedule") {
        const r = await authFetch(`${API}/api/policy/${slug}/control-test`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            test_ref: testRef,
            title,
            description_md: descriptionMd,
            due_by: new Date(dueBy).toISOString(),
            reason,
          }),
        });
        if (!r.ok) throw new Error(`HTTP ${r.status} — ${await r.text()}`);
        const j = await r.json();
        setSuccess(`Scheduled ${j.test_ref} due ${j.due_by}`);
      } else {
        const r = await authFetch(
          `${API}/api/policy/control-test/${controlTestId}/record`,
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              outcome, tested_by: testedBy,
              evidence_link: evidenceLink || null,
              notes_md: notesMd || null,
              reason,
            }),
          },
        );
        if (!r.ok) throw new Error(`HTTP ${r.status} — ${await r.text()}`);
        const j = await r.json();
        setSuccess(`Recorded outcome ${j.outcome} for ${controlTestId}`);
      }
      setTimeout(onClose, 1500);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div data-testid="control-test-modal" className="bg-white rounded shadow-lg w-full max-w-2xl max-h-full overflow-auto">
        <div className="flex items-center justify-between p-3 border-b">
          <h3 className="font-semibold flex items-center gap-2"><Activity size={16} /> Control test</h3>
          <button type="button" onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X size={16} />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div className="flex gap-2">
            <button
              type="button"
              data-testid="control-test-mode-schedule"
              onClick={() => setMode("schedule")}
              className={"px-3 py-1.5 text-sm border rounded " +
                (mode === "schedule" ? "bg-gray-900 text-white" : "")}
            >
              Schedule
            </button>
            <button
              type="button"
              data-testid="control-test-mode-record"
              onClick={() => setMode("record")}
              className={"px-3 py-1.5 text-sm border rounded " +
                (mode === "record" ? "bg-gray-900 text-white" : "")}
            >
              Record outcome
            </button>
          </div>
          {mode === "schedule" ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium" htmlFor="ct-slug">Policy</label>
                  <select
                    id="ct-slug"
                    data-testid="control-test-slug"
                    value={slug}
                    onChange={(e) => setSlug(e.target.value)}
                    className="w-full mt-1 border rounded p-2"
                  >
                    <option value="aml-policy">aml-policy</option>
                    <option value="governance-policy">governance-policy</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium" htmlFor="ct-ref">Test reference</label>
                  <input
                    id="ct-ref"
                    data-testid="control-test-ref"
                    type="text"
                    value={testRef}
                    onChange={(e) => setTestRef(e.target.value)}
                    placeholder="e.g. AML-CT-01"
                    className="w-full mt-1 border rounded p-2"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium" htmlFor="ct-title">Title</label>
                <input
                  id="ct-title"
                  data-testid="control-test-title"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full mt-1 border rounded p-2"
                />
              </div>
              <div>
                <label className="text-sm font-medium" htmlFor="ct-desc">Description (markdown)</label>
                <textarea
                  id="ct-desc"
                  data-testid="control-test-description"
                  value={descriptionMd}
                  onChange={(e) => setDescriptionMd(e.target.value)}
                  rows={3}
                  className="w-full mt-1 border rounded p-2 font-mono text-sm"
                />
              </div>
              <div>
                <label className="text-sm font-medium" htmlFor="ct-due">Due by</label>
                <input
                  id="ct-due"
                  data-testid="control-test-due-by"
                  type="date"
                  value={dueBy}
                  onChange={(e) => setDueBy(e.target.value)}
                  className="w-full mt-1 border rounded p-2"
                />
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="text-sm font-medium" htmlFor="ct-id">Control test ID</label>
                <input
                  id="ct-id"
                  data-testid="control-test-id-input"
                  type="text"
                  value={controlTestId}
                  onChange={(e) => setControlTestId(e.target.value)}
                  placeholder="Mongo ObjectId"
                  className="w-full mt-1 border rounded p-2 font-mono text-sm"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium" htmlFor="ct-outcome">Outcome</label>
                  <select
                    id="ct-outcome"
                    data-testid="control-test-outcome"
                    value={outcome}
                    onChange={(e) => setOutcome(e.target.value)}
                    className="w-full mt-1 border rounded p-2"
                  >
                    <option value="pass">pass</option>
                    <option value="fail">fail</option>
                    <option value="partial">partial</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium" htmlFor="ct-tested-by">Tested by (email)</label>
                  <input
                    id="ct-tested-by"
                    data-testid="control-test-tested-by"
                    type="email"
                    value={testedBy}
                    onChange={(e) => setTestedBy(e.target.value)}
                    className="w-full mt-1 border rounded p-2"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium" htmlFor="ct-evidence">Evidence link (optional)</label>
                <input
                  id="ct-evidence"
                  data-testid="control-test-evidence-link"
                  type="url"
                  value={evidenceLink}
                  onChange={(e) => setEvidenceLink(e.target.value)}
                  className="w-full mt-1 border rounded p-2"
                />
              </div>
              <div>
                <label className="text-sm font-medium" htmlFor="ct-notes">Notes (markdown)</label>
                <textarea
                  id="ct-notes"
                  data-testid="control-test-notes"
                  value={notesMd}
                  onChange={(e) => setNotesMd(e.target.value)}
                  rows={3}
                  className="w-full mt-1 border rounded p-2 font-mono text-sm"
                />
              </div>
            </>
          )}
          <div>
            <label className="text-sm font-medium" htmlFor="ct-reason">Reason (required)</label>
            <input
              id="ct-reason"
              data-testid="control-test-reason"
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full mt-1 border rounded p-2"
            />
          </div>
          {error && (
            <div data-testid="control-test-error" className="text-sm text-red-700 flex items-center gap-1">
              <AlertCircle size={14} /> {error}
            </div>
          )}
          {success && (
            <div data-testid="control-test-success" className="text-sm text-green-700">{success}</div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              data-testid="control-test-cancel"
              onClick={onClose}
              className="px-3 py-1.5 text-sm border rounded"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="control-test-submit"
              onClick={submit}
              disabled={submitting}
              className="px-3 py-1.5 text-sm border rounded bg-gray-900 text-white disabled:opacity-50"
            >
              {mode === "schedule" ? "Schedule test" : "Record outcome"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
